import { Ad as BaseAd, type RenderEvent, useAdsContext, useAdsTargeting } from '@folklore/ads';
import { useIsVisible } from '@folklore/hooks';
import classNames from 'classnames';
import { useState, ReactNode, ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';

import AdWrapper from './AdWrapper';

import styles from '../../styles/ads/ad.module.css';

export interface AdProps extends Omit<ComponentProps<typeof BaseAd>, 'slot'> {
    title?: ReactNode;
    withoutBorder?: boolean;
    disabled?: boolean;
    className?: string | null;
    emptyClassName?: string | null;
    loading?: 'lazy' | null;
    onRender?: (event: RenderEvent) => void;
    slot?: string | null;
    isNative?: boolean;
    [key: string]: unknown;
}

function Ad({
    title = null,
    withoutBorder = false,
    disabled = false,
    className = null,
    emptyClassName = null,
    loading = null,
    onRender = null,
    slot = null,
    isNative = false,
    ...props
}: AdProps) {
    const { ads: adsManager } = useAdsContext();
    const [renderEvent, setRenderEvent] = useState(null);
    const { isEmpty = true, isFluid = false } = renderEvent || {};
    const [wrapper, setWrapper] = useState(null);
    const { ref: refVisible, visible: isVisible = false } = useIsVisible({
        disabled: loading !== 'lazy',
        persist: true,
    });
    // const {
    //     ad: parallax,
    //     setAd: setParallaxAd,
    //     style: parallaxAdStyle,
    //     adContainerRef,
    //     blurBackground,
    // } = useParallaxAd();
    // const isParallax = parallax !== null;

    const [lastRenderedSize, setLastRenderedSize] = useState(null);
    const onAdRender = (event) => {
        setRenderEvent(event);
        const { isEmpty: newIsEmpty = true, width: newWidth, height: newHeight } = event || {};
        const isRendered = !newIsEmpty;
        setRichAd(null);

        setLastRenderedSize(
            isRendered
                ? {
                      width: newWidth,
                      height: newHeight,
                  }
                : null,
        );
        if (
            isRendered &&
            slot !== null &&
            slot.match(/_native$/) !== null &&
            ((newWidth === 1920 && newHeight === 1080) ||
                (newWidth === 1080 && newHeight === 1920) ||
                (newWidth === 740 && newHeight === 750) ||
                (newWidth === 420 && newHeight === 640) ||
                (newWidth === 1024 && newHeight === 750))
        ) {
            setWrapper({
                wrapper: 'parallax',
                width: newWidth,
                height: newHeight,
                format: 'contain',
                maskHeight: '100%',
                // backgroundColor: '#000',
            });
        } else {
            setWrapper(null);
        }
        if (onRender !== null) {
            onRender(event);
        }
    };

    const [richAd, setRichAd] = useState(null);
    const onRichAd = (richAd) => {
        setRichAd(richAd);
        const { type = null } = richAd || {};
        if (type === 'wrapper') {
            setWrapper(richAd);
        } else {
            setWrapper(null);
        }
    };
    const { type = null } = richAd || {};
    const isCard = type !== null && type.match(/_card$/) !== null;

    const contextTargeting = useAdsTargeting();
    const { disabled: targetingDisabled = false } = contextTargeting || {};
    const finalDisabled = adsManager.isDisabled() || targetingDisabled || disabled;

    const isRendered = !isEmpty && !finalDisabled;
    const keepSize = lastRenderedSize !== null && !isRendered;

    return (
        <div
            className={classNames([
                styles.container,
                {
                    // [styles.isParallax]: isParallax,
                    [styles.isDisabled]: finalDisabled && !keepSize,
                    [styles.isRendered]: renderEvent !== null,
                    [styles.isFluid]: isFluid,
                    [styles.isNative]: isNative,
                    [styles.isEmpty]: isEmpty && renderEvent !== null && !keepSize,
                    [styles.hasWrapper]: wrapper !== null,
                    [styles.withoutBorder]: withoutBorder,
                    [emptyClassName]: emptyClassName !== null && isEmpty && renderEvent !== null,
                    [className]: className !== null,
                },
            ])}
            ref={refVisible}
        >
            <div className={styles.inner}>
                <div className={styles.title}>
                    {title || (
                        <FormattedMessage defaultMessage="Publicité" description="Ad title" />
                    )}
                </div>
                <AdWrapper
                    wrapper={wrapper}
                    className={styles.center}
                    innerClassName={styles.adContainer}
                    transformClassName={styles.adTransform}
                >
                    <BaseAd
                        {...props}
                        slot={slot}
                        disabled={disabled || (loading === 'lazy' && !isVisible)}
                        // alwaysRender={alwaysRender || isVisible}
                        withReactId
                        shouldKeepSize
                        className={styles.ad}
                        richAdClassName={classNames([{ [styles.richAd]: wrapper === null }])}
                        richAdIframeClassName={classNames([
                            { [styles.richAdIframe]: wrapper === null },
                        ])}
                        onRender={onAdRender}
                        onRichAd={onRichAd}
                    />
                </AdWrapper>
            </div>
        </div>
    );
}

export default Ad;
